import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import BreadCrumbs from '../../components/BreadCrumbs';
import ClaimProviderForm from '../../components/ClaimProviderForm';
import StatusHandler from '../../components/StatusHandler';
import useApi from '../../hooks/useApi';
import {
  ClaimProvider,
  ClaimProviderDetailResponse,
  ClaimProviderUpdatePayload,
} from '../../models/claimProvider';

const ClaimProviderEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<ClaimProvider>();
  const [submissionError, setSubmissionError] = useState<string>();
  const [submissionSuccess, setSubmissionSuccess] = useState<string>();

  const {
    response,
    isLoading,
    error,
    launchCall,
  } = useApi<ClaimProviderDetailResponse>(
    `admin/provider-discovery/providers/${id}`,
  );
  const {
    launchCall: launchUpdate,
    isLoading: isSubmitting,
  } = useApi<ClaimProviderDetailResponse>(
    `admin/provider-discovery/providers/${id}`,
  );

  useEffect(() => {
    if (!id) return;
    launchCall({ method: 'GET' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (response?.data) {
      setProvider(response.data);
    }
  }, [response]);

  const handleSubmit = async (payload: ClaimProviderUpdatePayload) => {
    setSubmissionError(undefined);
    setSubmissionSuccess(undefined);
    const result = await launchUpdate({
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    if (!result?.data) {
      setSubmissionError('Failed to update provider. Please try again.');
      toast.error('Failed to update provider.');
      return;
    }
    setProvider(result.data);
    setSubmissionSuccess('Provider updated successfully.');
    toast.success('Provider updated successfully.');
  };

  const breadCrumbs = [
    { link: '/claim-providers', displayName: 'Claim Provider' },
    {
      link: id ? `/claim-providers/${id}` : undefined,
      displayName: provider?.name || 'Provider',
    },
    { displayName: 'Edit' },
  ];

  return (
    <div className="bg-[#f7faff] py-6 px-4 min-h-screen space-y-4">
      <BreadCrumbs breadcrumbs={breadCrumbs} />
      <StatusHandler
        isLoading={isLoading}
        error={error ? 'Failed to load provider details.' : undefined}>
        {provider ? (
          <ClaimProviderForm
            provider={provider}
            isSubmitting={isSubmitting}
            submissionError={submissionError}
            submissionSuccess={submissionSuccess}
            onCancel={() => navigate(`/claim-providers/${id}`)}
            onSubmit={handleSubmit}
          />
        ) : (
          <p className="text-gray-500">Provider not found.</p>
        )}
      </StatusHandler>
    </div>
  );
};

export default ClaimProviderEditPage;
